import { useTranslation } from "react-i18next";
import { SectionLabel } from "./primitives";

/**
 * [05] Field timeline. Horizontal strip of mini-tile thumbnails for
 * a single field across a season. Each card carries date + anomaly
 * count; a sparkline on the right shows the trend.
 */

type NoteKey = "baseline" | "preRain" | "stressSpike" | "postSpray" | "recovery";

type Snap = {
  date: string;
  count: number;
  severity: "low" | "mid" | "high";
  noteKey?: NoteKey;
};

const SCANS: Snap[] = [
  { date: "2026-04-12", count: 3,  severity: "low",  noteKey: "baseline" },
  { date: "2026-04-26", count: 5,  severity: "low" },
  { date: "2026-05-08", count: 7,  severity: "mid",  noteKey: "preRain" },
  { date: "2026-05-15", count: 14, severity: "high", noteKey: "stressSpike" },
  { date: "2026-05-23", count: 8,  severity: "mid",  noteKey: "postSpray" },
  { date: "2026-06-04", count: 6,  severity: "low" },
  { date: "2026-06-16", count: 4,  severity: "low",  noteKey: "recovery" },
];

export function Timeline() {
  const { t } = useTranslation();
  const max = Math.max(...SCANS.map((s) => s.count));
  const peak = Math.max(...SCANS.map((s) => s.count));
  const mean = (SCANS.reduce((a, s) => a + s.count, 0) / SCANS.length).toFixed(1);
  const delta = SCANS[SCANS.length - 1]!.count - SCANS[0]!.count;
  return (
    <section className="relative border-b border-[color:var(--color-app-edge)] bg-[color:var(--color-app-card)]/30">
      <div className="relative mx-auto max-w-[1320px] px-6 lg:px-10 py-24 md:py-28">
        <SectionLabel
          index={t("timeline.section.index")}
          title={t("timeline.section.title")}
          kicker={t("timeline.section.kicker")}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
          <h2 className="text-display text-5xl md:text-6xl">
            {t("timeline.headline.before")}{" "}
            <span className="text-accent text-accent-leaf">
              {t("timeline.headline.accent")}
            </span>
            {t("timeline.headline.after")}
          </h2>
          <div className="md:pl-8 md:border-l md:border-[color:var(--color-app-edge)] text-[color:var(--color-app-text)]/75 leading-relaxed flex items-center">
            <p>{t("timeline.intro")}</p>
          </div>
        </div>

        <div className="border border-[color:var(--color-app-edge-2)] bg-[color:var(--color-app-bg)]">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[color:var(--color-app-edge)] coord-mono">
            <span className="opacity-70">{t("timeline.field")}</span>
            <span className="opacity-50 hidden sm:inline">{t("timeline.deltaCaption")}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px]">
            {/* Scan strip */}
            <div className="p-6 overflow-x-auto scrollbar-fine">
              <div className="flex gap-3 min-w-max">
                {SCANS.map((s, i) => (
                  <ScanCard key={s.date} snap={s} max={max} step={i} />
                ))}
              </div>
            </div>

            {/* Sparkline panel */}
            <div className="border-t lg:border-t-0 lg:border-l border-[color:var(--color-app-edge)] p-6 bg-[color:var(--color-app-card)]/40">
              <div className="label-mono opacity-60 mb-2">{t("timeline.trend")}</div>
              <Sparkline data={SCANS.map((s) => s.count)} />
              <div className="mt-5 grid grid-cols-2 gap-y-2 coord-mono">
                <span className="opacity-60">{t("timeline.stats.peak")}</span>
                <span className="text-right num">{t("timeline.stats.peakValue", { count: peak })}</span>
                <span className="opacity-60">{t("timeline.stats.mean")}</span>
                <span className="text-right num">{t("timeline.stats.meanValue", { value: mean })}</span>
                <span className="opacity-60">{t("timeline.stats.delta")}</span>
                <span className="text-right num">{delta >= 0 ? "+" : ""}{delta}</span>
                <span className="opacity-60">{t("timeline.stats.confirmed")}</span>
                <span className="text-right num">{t("timeline.stats.confirmedValue")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScanCard({ snap, max, step }: { snap: Snap; max: number; step: number }) {
  const { t } = useTranslation();
  const heat = snap.severity === "high" ? "text-[color:var(--color-rust)]" :
               snap.severity === "mid"  ? "text-[color:var(--color-amber)]" :
                                          "text-[color:var(--color-leaf)]";
  const dot = snap.severity === "high" ? "bg-[color:var(--color-rust)]" :
              snap.severity === "mid"  ? "bg-[color:var(--color-amber)]" :
                                          "bg-[color:var(--color-leaf)]";
  return (
    <div className="w-[156px] flex-shrink-0 border border-[color:var(--color-app-edge)] bg-[color:var(--color-app-card)]/30">
      <div className="aspect-square relative overflow-hidden">
        <MiniTile seed={step} severity={snap.severity} />
        <div className="absolute top-1.5 left-1.5 flex items-center gap-1 coord-mono bg-[color:var(--color-app-bg)]/80 px-1.5 py-0.5">
          <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
          {snap.count}
        </div>
      </div>
      <div className="px-2.5 py-2 border-t border-[color:var(--color-app-edge)]">
        <div className="coord-mono">{snap.date}</div>
        {snap.noteKey ? (
          <div className={`text-[11px] mt-0.5 ${heat}`}>{t(`timeline.notes.${snap.noteKey}`)}</div>
        ) : (
          <div className="text-[11px] mt-0.5 opacity-50">—</div>
        )}
        <div className="mt-1.5 h-1 bg-[color:var(--color-app-edge)]">
          <div className={dot + " h-1"} style={{ width: `${(snap.count / max) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

function MiniTile({ seed, severity }: { seed: number; severity: Snap["severity"] }) {
  // Pseudo-random offsets so each tile looks unique but deterministic.
  const offs = [
    [0.20, 0.32], [0.40, 0.55], [0.65, 0.28], [0.30, 0.70], [0.75, 0.65],
    [0.18, 0.78], [0.55, 0.18],
  ];
  const [ox, oy] = offs[seed % offs.length]!;
  const tone = severity === "high" ? "#b54a2a" : severity === "mid" ? "#d97706" : "#5fb3c4";
  const blots = severity === "high" ? 4 : severity === "mid" ? 2 : 1;
  return (
    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`mt-${seed}-a`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#5a8543" />
          <stop offset="100%" stopColor="#3d5c2d" />
        </linearGradient>
        <linearGradient id={`mt-${seed}-b`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#7ba34d" />
          <stop offset="100%" stopColor="#557735" />
        </linearGradient>
        <pattern id={`mt-${seed}-rows`} patternUnits="userSpaceOnUse" width="3" height="100" patternTransform={`rotate(${(seed * 17) % 60 - 30})`}>
          <line x1="0" y1="0" x2="0" y2="100" stroke="rgba(20,30,10,0.18)" strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect x={0} y={0} width={100} height={100} fill={`url(#mt-${seed}-a)`} />
      <polygon points={`0,${20 + seed * 3} 100,${10 + seed * 2} 100,${55 + seed} 0,${65 + seed}`} fill={`url(#mt-${seed}-b)`} opacity="0.85" />
      <rect x={0} y={0} width={100} height={100} fill={`url(#mt-${seed}-rows)`} />
      {Array.from({ length: blots }).map((_, i) => (
        <ellipse
          key={i}
          cx={ox * 100 + i * 15 - blots * 5}
          cy={oy * 100 + i * 6}
          rx={10 - i * 1.5}
          ry={6 - i * 0.8}
          fill={tone}
          opacity={0.45}
        />
      ))}
    </svg>
  );
}

function Sparkline({ data }: { data: number[] }) {
  const w = 280;
  const h = 90;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const pts = data.map((d, i) => [i * step, h - ((d - min) / range) * (h - 12) - 6] as const);
  const path = "M " + pts.map((p) => `${p[0]} ${p[1]}`).join(" L ");
  const areaPath = path + ` L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[90px]">
      <defs>
        <linearGradient id="spark-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--color-amber)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="var(--color-amber)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#spark-fill)" />
      <path d={path} fill="none" stroke="var(--color-amber)" strokeWidth="1.5" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={2.5} fill="var(--color-app-bg)" stroke="var(--color-amber)" strokeWidth="1.2" />
      ))}
      {/* Baseline */}
      <line x1={0} y1={h - 2} x2={w} y2={h - 2} stroke="currentColor" strokeOpacity="0.15" />
    </svg>
  );
}
